/**
 * En qué va la sincronización, mientras dura.
 *
 * Antes la pantalla solo podía decir «hay una sincronización en curso» y el
 * nombre de la ejecución. Durante diez minutos eso es indistinguible de estar
 * colgado, así que la pregunta razonable —¿va bien? ¿cuánto falta?— no tenía
 * respuesta en ninguna parte.
 *
 * La barra se llena por FASES, no por tiempo, y el rótulo lo dice: «fase 2 de
 * 7». Las fases no duran lo mismo —la extracción se lleva la mayor parte—, así
 * que un porcentaje calculado sobre ellas se quedaría clavado en el 28 %
 * durante cinco minutos y parecería atascado. Para el tiempo está lo de al
 * lado: transcurrido, y lo que suele tardar, medido sobre las corridas
 * anteriores y no sobre una constante escrita a mano.
 */

import type { SyncProgress } from "../api/types";
import { StatusBadge } from "./StatusBadge";

/** `185` → `3m 05s`. Segundos sueltos por debajo del minuto. */
function duracion(segundos: number): string {
  const s = Math.max(0, Math.round(segundos));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, "0")}s`;
}

export function SyncProgressLine({ run }: { run: SyncProgress }) {
  const total = Math.max(1, run.phase_total);
  const hechas = Math.min(run.phase_index, total);
  const tarde = run.typical_seconds !== null && run.elapsed_seconds > run.typical_seconds * 1.5;

  return (
    <div className="card banner progreso">
      {/* `warning` es el tono de «en curso» en `runTone`, y se mantiene para
          que el aviso diga lo mismo aquí que en la tabla de abajo. */}
      <StatusBadge tone={tarde ? "serious" : "warning"}>En curso</StatusBadge>
      <div className="banner-body">
        <div className="progreso-cabeza">
          <strong>{run.phase_label}</strong>
          <span className="muted small">
            Fase {hechas} de {total}
          </span>
        </div>

        {/* `role="progressbar"` con sus valores: quien use lector de pantalla
            oye el avance, no solo un rectángulo que crece. La unidad son fases,
            que es lo que de verdad se está midiendo. */}
        <div
          className="progreso-barra"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={hechas}
          aria-valuetext={`Fase ${hechas} de ${total}: ${run.phase_label}`}
        >
          <span style={{ width: `${(hechas / total) * 100}%` }} />
        </div>

        <p className="progreso-pie small">
          <span>{duracion(run.elapsed_seconds)} transcurridos</span>
          {run.typical_seconds !== null && (
            <>
              <span aria-hidden="true"> · </span>
              <span className="muted">
                suele tardar {duracion(run.typical_seconds)}
              </span>
            </>
          )}
          {run.triggered_by && (
            <>
              <span aria-hidden="true"> · </span>
              <span className="muted">solicitada por {run.triggered_by}</span>
            </>
          )}
        </p>

        {tarde && (
          <p className="small">
            Está tardando más de lo habitual. No es un fallo por sí solo —una
            corrida puede crecer con el volumen—, pero conviene mirar la
            ejecución si sigue así.
          </p>
        )}

        {run.execution && <p className="mono small muted">{run.execution}</p>}
      </div>
    </div>
  );
}
